import { Activity, Clock3, Database, Radio, Settings2, Tags } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { fmtDate, nextActionText, processStateText } from "@/lib/format";

const CONNECTION = {
  online: { nl: "Online", en: "Online", tone: "text-emerald-500", dot: "bg-emerald-500" },
  temporarily_disconnected: { nl: "Tijdelijk geen verbinding", en: "Temporarily disconnected", tone: "text-amber-500", dot: "bg-amber-500" },
  offline: { nl: "Offline", en: "Offline", tone: "text-red-500", dot: "bg-red-500" },
};

function StatusItem({ testId, icon: Icon, label, value, tone = "text-foreground" }) {
  return (
    <div className="min-w-0 border-b border-border px-4 py-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`mt-1 truncate font-heading text-sm font-bold ${tone}`}>{value || "—"}</div>
    </div>
  );
}

export function ProtocolStatus({ overview }) {
  const { t, lang } = useLang();
  if (!overview) return null;
  const connection = CONNECTION[overview.connectivity] || CONNECTION.offline;
  const connectionText = lang === "en" ? connection.en : connection.nl;
  return (
    <section className="space-y-2" data-testid="protocol-v1-status-section">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${connection.dot}`} />
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Device API v1</h2>
        <span className="font-mono text-[10px] text-muted-foreground" data-testid="protocol-external-device-id">{overview.external_device_id}</span>
      </div>
      <div className="grid overflow-hidden rounded-lg border border-border bg-card sm:grid-cols-2 lg:grid-cols-6">
        <StatusItem testId="protocol-connectivity" icon={Radio} label={t("Verbinding", "Connection")} value={connectionText} tone={connection.tone} />
        <StatusItem testId="protocol-process-state" icon={Activity} label={t("Proces", "Process")} value={processStateText(overview.process_state, lang)} />
        <StatusItem testId="protocol-next-action" icon={Clock3} label={t("Volgende actie", "Next action")} value={nextActionText(overview.next_action, lang)} />
        <StatusItem testId="protocol-last-measurement" icon={Database} label={t("Laatste meting", "Last measurement")} value={fmtDate(overview.last_measurement_at, lang)} />
        <StatusItem testId="protocol-queue-count" icon={Tags} label={t("Lokale wachtrij", "Local queue")} value={String(overview.queue_count ?? 0)} />
        <StatusItem testId="protocol-config-version" icon={Settings2} label={t("Config / software", "Config / software")} value={`v${overview.active_config_version ?? 0} · ${overview.software_version || "—"}`} />
      </div>
    </section>
  );
}
